import { SimWorld, addLog, isGroupModeEnabled, isGroupFixedPopulation } from '../types/SimWorld';
import { SimNPC, createSimNPC, isCardForbidden, MovementType, ATTRIBUTE_SEEKER_TYPES } from '../types/SimNPC';
import { GameMap } from '../types/GameMap';
import { placeNPCOnMap } from '../map/PositionUtils';
import { generateFirstName, extractLastName } from '../utils/NameGenerator';
import { getAvailableCards, CardPoolConfig } from '../utils/CardFilter';
import type { ExtendedCardData } from '../../utils/cardManager';
import type { CardWithId } from '../../types/card';
import {
  POPULATION_CONSTANTS,
  IMMIGRANT_SURNAMES,
} from '../core/PopulationConstants';
import type { GroupId, GroupConfig } from '../types/Group';

const {
  IMMIGRATION_INTERVAL,
  IMMIGRATION_BASE_COUNT,
  IMMIGRATION_POPULATION_FACTOR,
} = POPULATION_CONSTANTS;

/**
 * 属性リスト
 */
const ATTRIBUTES = ['炎', '水', '光', '闇', '原始', '未来'];

/**
 * シーカータイプから属性を取得
 */
function getSeekerAttribute(movementType: MovementType): string | null {
  switch (movementType) {
    case 'fire_seeker': return '炎';
    case 'water_seeker': return '水';
    case 'light_seeker': return '光';
    case 'dark_seeker': return '闇';
    case 'primitive_seeker': return '原始';
    case 'future_seeker': return '未来';
    default: return null;
  }
}

/**
 * 移住者用の移動タイプをランダム選択
 * シーカータイプ優先（70%）
 */
function assignImmigrantMovementType(): MovementType {
  // 70%の確率でシーカータイプ
  if (Math.random() < 0.7) {
    return ATTRIBUTE_SEEKER_TYPES[Math.floor(Math.random() * ATTRIBUTE_SEEKER_TYPES.length)];
  }

  // 30%の確率で通常タイプ
  const normalTypes: MovementType[] = ['random', 'explorer', 'social', 'avoider'];
  return normalTypes[Math.floor(Math.random() * normalTypes.length)];
}

/**
 * 属性特化デッキ生成結果
 */
interface AttributeFocusedDeckResult {
  deck: CardWithId[];
  mainAttribute: string;
}

/**
 * 属性特化デッキ生成（移住者用）
 * 30枚を主属性、10枚をサブ属性/汎用で構成
 * 禁忌カードは1枚まで
 * @param allCards 全カードリスト
 * @param specifiedAttribute 指定属性（シーカーの場合）
 * @param cardPoolConfig カードプール設定（除外カード等）
 */
function createAttributeFocusedDeck(
  allCards: ExtendedCardData[],
  specifiedAttribute?: string,
  cardPoolConfig?: CardPoolConfig
): AttributeFocusedDeckResult {
  // カードプール設定を適用
  const availableCards = getAvailableCards(allCards, cardPoolConfig);

  const deck: CardWithId[] = [];
  let forbiddenCount = 0;
  const cardCounts = new Map<string, number>(); // 同名カード枚数管理

  // 主属性: 指定があればそれを使用、なければランダム
  const mainAttribute = specifiedAttribute || ATTRIBUTES[Math.floor(Math.random() * ATTRIBUTES.length)];

  // サブ属性をランダム選択（主属性と異なる）
  const otherAttributes = ATTRIBUTES.filter(a => a !== mainAttribute);
  const subAttribute = otherAttributes[Math.floor(Math.random() * otherAttributes.length)];

  // 主属性カードを抽出（利用可能なカードから）
  const mainAttributeCards = availableCards.filter(c => c.attribute === mainAttribute);
  // サブ属性カードを抽出
  const subAttributeCards = availableCards.filter(c => c.attribute === subAttribute);
  // 汎用カード（なし属性）
  const neutralCards = availableCards.filter(c => c.attribute === 'なし' || !c.attribute);

  // 主属性30枚を追加
  const mainCount = 30;
  for (let i = 0; i < mainCount; i++) {
    const sourceCards = mainAttributeCards.length > 0 ? mainAttributeCards : availableCards;

    // 3枚制限 + 禁忌カード制限を適用
    let validSourceCards = sourceCards.filter(c => {
      const currentCount = cardCounts.get(c.id) || 0;
      if (currentCount >= 3) return false; // 3枚制限
      if (isCardForbidden(c) && forbiddenCount >= 1) return false; // 禁忌カード制限
      return true;
    });

    // 有効なカードがない場合は全カードから選択（フォールバック）
    const finalSourceCards = validSourceCards.length > 0 ? validSourceCards : sourceCards;

    const card = finalSourceCards[Math.floor(Math.random() * finalSourceCards.length)];

    cardCounts.set(card.id, (cardCounts.get(card.id) || 0) + 1);
    if (isCardForbidden(card)) {
      forbiddenCount++;
    }

    deck.push({
      ...card,
      uniqueId: `${card.id}_imm_${i}_${Math.random()}`,
    });
  }

  // 残り10枚はサブ属性または汎用
  const subCount = 10;
  const subSourceCards = [...subAttributeCards, ...neutralCards];
  const actualSubSource = subSourceCards.length > 0 ? subSourceCards : availableCards;

  for (let i = 0; i < subCount; i++) {
    // 3枚制限 + 禁忌カード制限を適用
    let validSubSource = actualSubSource.filter(c => {
      const currentCount = cardCounts.get(c.id) || 0;
      if (currentCount >= 3) return false; // 3枚制限
      if (isCardForbidden(c) && forbiddenCount >= 1) return false; // 禁忌カード制限
      return true;
    });

    // 有効なカードがない場合は全カードから選択（フォールバック）
    const finalSubSource = validSubSource.length > 0 ? validSubSource : actualSubSource;

    const card = finalSubSource[Math.floor(Math.random() * finalSubSource.length)];

    cardCounts.set(card.id, (cardCounts.get(card.id) || 0) + 1);
    if (isCardForbidden(card)) {
      forbiddenCount++;
    }

    deck.push({
      ...card,
      uniqueId: `${card.id}_imm_${mainCount + i}_${Math.random()}`,
    });
  }

  return { deck, mainAttribute };
}

/**
 * ランダムデッキ生成（旧方式・バックアップ用）
 * 禁忌カードは1枚まで、同名カードは3枚まで
 * @param allCards 全カードリスト
 * @param cardPoolConfig カードプール設定（除外カード等）
 */
function createRandomDeck(
  allCards: ExtendedCardData[],
  cardPoolConfig?: CardPoolConfig
): CardWithId[] {
  // カードプール設定を適用
  const availableCards = getAvailableCards(allCards, cardPoolConfig);

  const deck: CardWithId[] = [];
  let forbiddenCount = 0;
  const cardCounts = new Map<string, number>(); // 同名カード枚数管理

  for (let i = 0; i < 40; i++) {
    // 3枚制限 + 禁忌カード制限を適用
    const validCards = availableCards.filter(c => {
      const currentCount = cardCounts.get(c.id) || 0;
      if (currentCount >= 3) return false; // 3枚制限
      if (isCardForbidden(c) && forbiddenCount >= 1) return false; // 禁忌カード制限
      return true;
    });

    // 有効なカードがない場合は全カードから選択（フォールバック）
    const finalCards = validCards.length > 0 ? validCards : availableCards;

    const card = finalCards[Math.floor(Math.random() * finalCards.length)];

    cardCounts.set(card.id, (cardCounts.get(card.id) || 0) + 1);
    if (isCardForbidden(card)) {
      forbiddenCount++;
    }

    const cardCopy: CardWithId = {
      ...card,
      uniqueId: `${card.id}_imm_${i}_${Math.random()}`,
    };
    deck.push(cardCopy);
  }
  return deck;
}

/**
 * 既存NPCの苗字リストを取得
 */
function getExistingSurnames(npcs: SimNPC[]): Set<string> {
  const surnames = new Set<string>();
  for (const npc of npcs) {
    const surname = extractLastName(npc.name);
    if (surname) {
      surnames.add(surname);
    }
  }
  return surnames;
}

/**
 * 移住者用のユニークな苗字を選択
 * 既存の苗字と被らないものを優先
 */
function selectImmigrantSurname(existingSurnames: Set<string>): string {
  // 既存と被らない苗字を探す
  const availableSurnames = IMMIGRANT_SURNAMES.filter(
    (s) => !existingSurnames.has(s)
  );

  if (availableSurnames.length > 0) {
    return availableSurnames[
      Math.floor(Math.random() * availableSurnames.length)
    ];
  }

  // 全て使われている場合はランダムに選択
  return IMMIGRANT_SURNAMES[
    Math.floor(Math.random() * IMMIGRANT_SURNAMES.length)
  ];
}

/**
 * 現在の最多世代（モード）を取得
 * 移住者は既存NPCの世代に合わせる
 */
function getDominantGeneration(npcs: SimNPC[]): number {
  const aliveNpcs = npcs.filter((n) => n.isAlive);
  if (aliveNpcs.length === 0) return 1;

  // 世代ごとのカウントを取得
  const genCounts = new Map<number, number>();
  for (const npc of aliveNpcs) {
    const gen = npc.generation || 1;
    genCounts.set(gen, (genCounts.get(gen) || 0) + 1);
  }

  // 最多の世代を返す
  let dominantGen = 1;
  let maxCount = 0;
  for (const [gen, count] of genCounts) {
    if (count > maxCount) {
      maxCount = count;
      dominantGen = gen;
    }
  }

  return dominantGen;
}

/**
 * 移住者NPCを作成
 * @param groupId グループID（グループモード時）
 * @param groupConfig グループ設定（グループモード時）
 */
function createImmigrant(
  world: SimWorld,
  allCards: ExtendedCardData[],
  map: GameMap,
  groupId?: GroupId,
  groupConfig?: GroupConfig
): { immigrant: SimNPC; mainAttribute: string; isSeeker: boolean } {
  const existingNames = new Set(world.npcs.map((n) => n.name));
  const existingSurnames = getExistingSurnames(world.npcs);

  // 既存と異なる苗字を選択
  const surname = selectImmigrantSurname(existingSurnames);
  const firstName = generateFirstName();
  let name = `${surname} ${firstName}`;

  // 重複チェック
  let attempts = 0;
  while (existingNames.has(name) && attempts < 100) {
    const newFirstName = generateFirstName();
    name = `${surname} ${newFirstName}`;
    attempts++;
  }

  // それでも重複する場合は番号を付ける
  if (existingNames.has(name)) {
    let counter = 1;
    while (existingNames.has(`${name} ${counter}`)) {
      counter++;
    }
    name = `${name} ${counter}`;
  }

  // 移動タイプを決定（シーカー優先70%）
  const movementType = assignImmigrantMovementType();
  const seekerAttribute = getSeekerAttribute(movementType);
  const isSeeker = seekerAttribute !== null;

  // カードプール設定を決定（グループモードならグループの設定を使用）
  const cardPoolConfig = groupConfig?.cardPoolConfig || world.cardPoolConfig;

  // シーカーなら対応属性、それ以外はランダム属性のデッキを作成
  const { deck, mainAttribute } = createAttributeFocusedDeck(
    allCards,
    seekerAttribute || undefined,
    cardPoolConfig
  );

  // 移住者の世代は現在の最多世代に合わせる
  const immigrantGeneration = getDominantGeneration(world.npcs);

  const immigrant = createSimNPC(name, deck, {
    generation: immigrantGeneration,
    birthDay: world.day, // 移住日を誕生日として記録
    group: groupId, // グループを設定
  });

  // 移住者は成人状態でスタート（即座に繁殖可能）
  immigrant.age = 20;
  immigrant.daysLived = 20;
  immigrant.rating = Math.floor(Math.random() * 200) + 900; // 900-1100のレーティング

  // 移動タイプを設定
  immigrant.movementType = movementType;

  // マップ上にランダム配置
  const randomX = Math.floor(Math.random() * map.width);
  const randomY = Math.floor(Math.random() * map.height);
  immigrant.position = { x: randomX, y: randomY };
  placeNPCOnMap(immigrant, map);

  return { immigrant, mainAttribute, isSeeker };
}

/**
 * 移住処理
 * 30日ごとに外部から新しいNPCが移住し、遺伝的多様性を維持する
 * グループモードでは各グループに移住者が追加される
 */
export function processImmigration(
  world: SimWorld,
  allCards: ExtendedCardData[],
  map: GameMap
): void {
  const { day, npcs } = world;

  // 30日ごとに移住イベント
  if (day % IMMIGRATION_INTERVAL !== 0 || day === 0) return;

  const groupModeEnabled = isGroupModeEnabled(world);

  if (groupModeEnabled && world.groupModeConfig) {
    // グループモード: 各グループに移住者を追加
    processImmigrationGroupMode(world, allCards, map);
    return;
  }

  // 通常モード
  const aliveCount = npcs.filter((n) => n.isAlive).length;

  // 移住者数: 基本2人 + 人口の2%
  const immigrantCount = Math.floor(
    IMMIGRATION_BASE_COUNT + aliveCount * IMMIGRATION_POPULATION_FACTOR
  );

  const newImmigrants: { immigrant: SimNPC; mainAttribute: string; isSeeker: boolean }[] = [];

  for (let i = 0; i < immigrantCount; i++) {
    const result = createImmigrant(world, allCards, map);
    newImmigrants.push(result);
  }

  // 移住者を追加
  npcs.push(...newImmigrants.map(r => r.immigrant));

  // ログ出力（まとめて）
  if (newImmigrants.length === 1) {
    const { immigrant, mainAttribute, isSeeker } = newImmigrants[0];
    const typeLabel = isSeeker ? `${mainAttribute}属性の求道者` : `${mainAttribute}属性使い`;
    addLog(world, `🚶 ${immigrant.name}が外からやってきた！（${typeLabel}）`);
  } else {
    const details = newImmigrants.map(r => {
      const typeLabel = r.isSeeker ? `${r.mainAttribute}求道者` : `${r.mainAttribute}`;
      return `${r.immigrant.name}(${typeLabel})`;
    }).join('、');
    addLog(
      world,
      `🚶 ${newImmigrants.length}人の移住者がやってきた！（${details}）`
    );
  }
}

/**
 * グループモード用移住処理
 * 各グループに均等に移住者を追加
 */
function processImmigrationGroupMode(
  world: SimWorld,
  allCards: ExtendedCardData[],
  map: GameMap
): void {
  const { npcs, groupModeConfig, groupStats } = world;
  if (!groupModeConfig) return;

  const groups = groupModeConfig.groups;
  if (groups.length === 0) return;

  // 各グループの人口を計算
  const groupPopulations = new Map<GroupId, number>();
  for (const group of groups) {
    groupPopulations.set(group.id, 0);
  }
  for (const npc of npcs) {
    if (npc.isAlive && npc.group) {
      groupPopulations.set(npc.group, (groupPopulations.get(npc.group) || 0) + 1);
    }
  }

  // 各グループに移住者を追加（人口が少ないグループに多めに追加）
  const totalAlive = npcs.filter((n) => n.isAlive).length;
  const baseCount = Math.floor(IMMIGRATION_BASE_COUNT / groups.length) || 1;

  const newImmigrants: { immigrant: SimNPC; mainAttribute: string; isSeeker: boolean; groupId: GroupId }[] = [];

  for (const group of groups) {
    // 固定人口モードのグループは移住をスキップ（リスポーンで管理）
    if (isGroupFixedPopulation(group.id, world)) continue;

    const pop = groupPopulations.get(group.id) || 0;
    const avgPop = totalAlive / groups.length;

    // 人口が平均より少ないグループには追加の移住者
    let count = baseCount;
    if (pop < avgPop * 0.7) {
      count += 1;
    }

    for (let i = 0; i < count; i++) {
      const result = createImmigrant(world, allCards, map, group.id, group);
      newImmigrants.push({ ...result, groupId: group.id });

      // グループ統計を更新
      if (groupStats) {
        const stats = groupStats.get(group.id);
        if (stats) {
          stats.population++;
        }
      }
    }
  }

  // 移住者を追加
  npcs.push(...newImmigrants.map(r => r.immigrant));

  // ログ出力（グループ別）
  const groupSummary = groups.map(g => {
    const immigrants = newImmigrants.filter(i => i.groupId === g.id);
    return `${g.name}: ${immigrants.length}人`;
  }).join('、');

  addLog(world, `🚶 移住者がやってきた！（${groupSummary}）`);
}
